import { useEffect, useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/styles.css";
import {
  DocumentTextIcon,
  MapPinIcon,
  TruckIcon,
  UserCircleIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import { CogIcon } from "lucide-react";
import BoxWrapper from "../../components/ui/BoxWrapper";
import DocViewer from "../../components/ui/DocViewer";
import DriverDocument from "../../components/ui/DriverDocument";
import PromptModal from "../../components/modals/PromptModal";
import PillTabs, { PillTab } from "../../components/userDetail/PillTabs";

import {
  DriverProfileStatus,
  DriverProfileStatusDisplayValue,
} from "../../helpers/enums";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import AdminRoute from "../auth/RestrictedRoute";
import Balance from "./Balance";
import GeneralInfoForm from "./GeneralInfoForm";
import VehicleInfoForm from "./VehicleInfoForm";
import DriverRides from "./DriverRides";
import AppConfigurationTab from "./AppConfiguration";
import DriverDetailHeader from "./Driverdetailheader";

const HASHES = [
  "#generalInfo",
  "#vehicle",
  "#documents",
  "#solde",
  "#App",
  "#trajets",
];

const DRIVER_TABS: PillTab[] = [
  { key: 0, label: "Informations Générales", icon: UserCircleIcon },
  { key: 1, label: "Informations de Véhicule", icon: TruckIcon },
  { key: 2, label: "Documents", icon: DocumentTextIcon },
  { key: 3, label: "Solde", icon: WalletIcon },
  { key: 4, label: "Configuration de l'app", icon: CogIcon },
  { key: 5, label: "Trajets", icon: MapPinIcon },
];

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="pt-4 mb-3">
      <h5 className="fw-bold text-start mb-0">{children}</h5>
      <hr className="mt-1" />
    </div>
  );
}

export default function Driver() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useCookie("vToken");
  const [docIndex, setDocIndex] = useState(-1);
  const [openDocViewer, setOpenDocViewer] = useState(false);
  const [docToView, setDocToView] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const { hash, pathname, search } = useLocation();
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("");
  const driverId = params?.driverId;
  const [isOnline, setIsOnline] = useState(false);
  const { data } = useQuery<Driver>(
    ["getDriver", driverId],
    () => {
      return apiService.MakeGetRequest(`users/driver/${driverId}`, token);
    },
    { refetchOnWindowFocus: false },
  );

  const invalidateDriverDataQuery = useCallback(() => {
    queryClient.invalidateQueries(["getDriver", driverId]);
  }, [driverId, queryClient]);

  useEffect(() => {
    if (hash) {
      const index = HASHES.indexOf(hash);
      index >= 0 && setActiveTab(index);
    }
  }, [hash]);

  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
    navigate(`${pathname}${search}${HASHES[tabIndex] ?? ""}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (data?.driverProfile?.isOnline !== undefined) {
      setIsOnline(data.driverProfile.isOnline);
    }
  }, [data?.driverProfile?.isOnline]);

  const driverDocs = [
    {
      src: data?.driverProfile?.pictureCarteCinRecto,
      noImage: !data?.driverProfile?.pictureCarteCinRecto,
      name: "pictureCarteCinRecto",
      label: "Carte Identité National Recto",
      docStatus: data?.driverProfile?.pictureCarteCinRectoStatus,
      expires: data?.driverProfile?.carteCinExpiration,
      showExpirationField: true,
      expirationField: "carteCinExpiration",
    },
    {
      src: data?.driverProfile?.pictureCarteCinVerso,
      noImage: !data?.driverProfile?.pictureCarteCinVerso,
      name: "pictureCarteCinVerso",
      label: "Carte Identité National Verso",
      docStatus: data?.driverProfile?.pictureCarteCinVersoStatus,
      expires: data?.driverProfile?.carteCinExpiration,
      showExpirationField: false,
    },
    {
      src: data?.driverProfile?.picturePermisRecto,
      noImage: !data?.driverProfile?.picturePermisRecto,
      name: "picturePermisRecto",
      label: "Permis de conduire Recto",
      docStatus: data?.driverProfile?.picturePermisRectoStatus,
      expires: data?.driverProfile?.permisExpiration,
      showExpirationField: true,
      expirationField: "permisExpiration",
    },
    {
      src: data?.driverProfile?.picturePermisVerso,
      noImage: !data?.driverProfile?.picturePermisVerso,
      name: "picturePermisVerso",
      label: "Permis de conduire Verso",
      docStatus: data?.driverProfile?.picturePermisVersoStatus,
      expires: data?.driverProfile?.permisExpiration,
      showExpirationField: false,
    },
    {
      src: data?.driverProfile?.picturePermisConfianceRecto,
      noImage: !data?.driverProfile?.picturePermisConfianceRecto,
      name: "picturePermisConfianceRecto",
      label: "Permis de Confiance Recto",
      docStatus: data?.driverProfile?.picturePermisConfianceRectoStatus,
      expires: data?.driverProfile?.permisConfianceExpiration,
      showExpirationField: true,
      expirationField: "permisConfianceExpiration",
    },
    {
      src: data?.driverProfile?.picturePermisConfianceVerso,
      noImage: !data?.driverProfile?.picturePermisConfianceVerso,
      name: "picturePermisConfianceVerso",
      label: "Permis de Confiance Verso",
      docStatus: data?.driverProfile?.picturePermisConfianceVersoStatus,
      expires: data?.driverProfile?.permisConfianceExpiration,
      showExpirationField: false,
    },
    {
      src: data?.driverProfile?.pictureCarteConducteurProRecto,
      noImage: !data?.driverProfile?.pictureCarteConducteurProRecto,
      name: "pictureCarteConducteurProRecto",
      label: "Carte de Conducteur Professionnel Recto",
      docStatus: data?.driverProfile?.pictureCarteConducteurProRectoStatus,
      expires: data?.driverProfile?.carteConducteurExpiration,
      showExpirationField: true,
      expirationField: "carteConducteurExpiration",
    },
    {
      src: data?.driverProfile?.pictureCarteConducteurProVerso,
      noImage: !data?.driverProfile?.pictureCarteConducteurProVerso,
      name: "pictureCarteConducteurProVerso",
      label: "Carte de Conducteur Professionnel Verso",
      docStatus: data?.driverProfile?.pictureCarteConducteurProVersoStatus,
      expires: data?.driverProfile?.carteConducteurExpiration,
      showExpirationField: false,
    },
  ];

  const vehicleDocs = [
    {
      src: data?.driverProfile?.vehicle?.frontPicture,
      noImage: !data?.driverProfile?.vehicle?.frontPicture,
      name: "frontPicture",
      label: "Photo d'un taxi vu de face",
      docStatus: data?.driverProfile?.vehicle?.frontPictureStatus,
      expires: "",
      showExpirationField: false,
    },
    {
      src: data?.driverProfile?.vehicle?.sidePicture,
      noImage: !data?.driverProfile?.vehicle?.sidePicture,
      name: "sidePicture",
      label: "Photo d'un taxi de côté",
      docStatus: data?.driverProfile?.vehicle?.sidePictureStatus,
      expires: "",
      showExpirationField: false,
    },
    {
      src: data?.driverProfile?.vehicle?.pictureCarteGriseRecto,
      noImage: !data?.driverProfile?.vehicle?.pictureCarteGriseRecto,
      name: "pictureCarteGriseRecto",
      label: "Carte Grise Recto",
      docStatus: data?.driverProfile?.vehicle?.pictureCarteGriseRectoStatus,
      expires: data?.driverProfile?.vehicle?.carteGriseExpiration,
      showExpirationField: true,
      expirationField: "carteGriseExpiration",
    },
    {
      src: data?.driverProfile?.vehicle?.pictureCarteGriseVerso,
      noImage: !data?.driverProfile?.vehicle?.pictureCarteGriseVerso,
      name: "pictureCarteGriseVerso",
      label: "Carte Grise Verso",
      docStatus: data?.driverProfile?.vehicle?.pictureCarteGriseVersoStatus,
      expires: data?.driverProfile?.vehicle?.carteGriseExpiration,
      showExpirationField: false,
    },
  ];

  function getDriverStatusDisplayValue() {
    if (data?.driverProfile?.status === DriverProfileStatus.APPROVED) {
      return DriverProfileStatusDisplayValue.APPROVED;
    } else if (data?.driverProfile?.status === DriverProfileStatus.DECLINED) {
      return DriverProfileStatusDisplayValue.DECLINED;
    } else if (data?.driverProfile?.status === DriverProfileStatus.BLOCKED) {
      return DriverProfileStatusDisplayValue.BLOCKED;
    } else if (data?.driverProfile?.status === DriverProfileStatus.IN_REVIEW) {
      return DriverProfileStatusDisplayValue.IN_REVIEW;
    }
    return DriverProfileStatusDisplayValue.IN_REVIEW;
  }

  const AcceptDriver = useMutation((id: string) => {
    return apiService.MakePutRequest(`users/approve-profile/${id}`, {}, token);
  });

  const handleOnlineStatusChange = useMutation(
    (newStatus: string) => {
      return apiService.MakePutRequest(
        `users/toggle-driver-status/${data?.id}`,
        { newStatus },
        token,
      );
    },
    {
      onSuccess() {
        toastSuccess("Status mis à jour");
        setIsOnline(!isOnline);
      },
      onError() {
        toastError(general.fr.message.operationFailed);
      },
    },
  );

  const AcceptDriverButton = (id: string) => {
    AcceptDriver.mutate(id, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Chauffeur acceptée");
        invalidateDriverDataQuery();
      },
    });
  };

  const RefuseDriver = useMutation((id: string) => {
    return apiService.MakePutRequest(`users/decline-docs/${id}`, {}, token);
  });

  const RefuseDriverButton = (id: string) => {
    RefuseDriver.mutate(id, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Chauffeur refusée");
        queryClient.invalidateQueries(["getDriver"]);
      },
    });
  };

  const PendingDriver = useMutation((id: string) => {
    return apiService.MakePutRequest(`users/pending-profile/${id}`, {}, token);
  });

  const PendingDriverButton = (id: string) => {
    PendingDriver.mutate(id, {
      onError() {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess("Chauffeur en attente");
        invalidateDriverDataQuery();
      },
    });
  };

  interface docActionProps {
    action: string;
    id: string;
    docName: string;
    endPoint: string;
  }

  const handleDocAction = useMutation(
    ({ action, id, docName, endPoint }: docActionProps) => {
      return apiService.MakePutRequest(
        `${endPoint}/${action}-single-doc/${id}/${docName}`,
        {},
        token,
      );
    },
  );

  const handleDocActionButton = (
    action: string,
    id: string,
    docName: string,
    endPoint: string,
  ) => {
    handleDocAction.mutate(
      { action, id, docName, endPoint },
      {
        onError() {
          toastError(general.fr.message.operationFailed);
        },
        onSuccess() {
          toastSuccess(
            `Document ${action === "approve" ? "accepté" : "refusé"}`,
          );
          setShowModal(false);
          invalidateDriverDataQuery();
        },
      },
    );
  };

  function handleAccpetRefuse() {
    if (action === "approveDoc") {
      handleDocActionButton(
        "approve",
        data?.id ?? "",
        driverDocs?.[docIndex]?.name,
        "users",
      );
    }
    if (action === "declineDoc") {
      handleDocActionButton(
        "decline",
        data?.id ?? "",
        driverDocs?.[docIndex]?.name,
        "users",
      );
    }
    if (action === "approveVehicleDoc") {
      handleDocActionButton(
        "approve",
        data?.driverProfile?.vehicle?.id ?? "",
        vehicleDocs?.[docIndex]?.name,
        "vehicles",
      );
    }
    if (action === "declineVehicleDoc") {
      handleDocActionButton(
        "decline",
        data?.driverProfile?.vehicle?.id ?? "",
        vehicleDocs?.[docIndex]?.name,
        "vehicles",
      );
    }
    if (action === "approveProfile") {
      AcceptDriverButton(data?.id ?? "");
    }
    if (action === "declineProfile") {
      RefuseDriverButton(data?.id ?? "");
    }
    if (action === "pendingProfile") {
      PendingDriverButton(data?.id ?? "");
    }
    setShowModal(false);
  }

  const status = data?.driverProfile?.status;
  const noProfile = data?.driverProfile === null;
  const showApprove =
    status === DriverProfileStatus.IN_REVIEW ||
    status === DriverProfileStatus.DECLINED ||
    noProfile;
  const showDecline =
    status === DriverProfileStatus.IN_REVIEW ||
    status === DriverProfileStatus.APPROVED ||
    noProfile;
  const showPending =
    status === DriverProfileStatus.APPROVED ||
    status === DriverProfileStatus.DECLINED ||
    noProfile;

  const openAction = (name: string) => {
    setAction(name);
    setShowModal(true);
  };

  return (
    <AdminRoute>
      <section>
        {/* Tabs */}
        <PillTabs
          tabs={DRIVER_TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Driver header */}
        <DriverDetailHeader
          fullName={data?.fullName}
          profilePicture={data?.profilePicture as string | undefined}
          status={status}
          statusLabel={getDriverStatusDisplayValue()}
          isOnline={isOnline}
          onlineLoading={handleOnlineStatusChange.isLoading}
          onToggleOnline={(checked) =>
            handleOnlineStatusChange.mutate(checked ? "online" : "offline")
          }
          uploadHref={`/drivers/${data?.id}/upload-files`}
          LinkComponent={Link}
          showApprove={showApprove}
          showDecline={showDecline}
          showPending={showPending}
          approveLoading={AcceptDriver.isLoading}
          declineLoading={RefuseDriver.isLoading}
          pendingLoading={PendingDriver.isLoading}
          actionsDisabled={driverDocs?.length === 0}
          onApprove={() => openAction("approveProfile")}
          onDecline={() => openAction("declineProfile")}
          onPending={() => openAction("pendingProfile")}
        />

        {/* Tab content */}
        {activeTab === 0 && (
          <GeneralInfoForm
            data={data}
            role="driver"
            invalidateQuery={invalidateDriverDataQuery}
          />
        )}

        {activeTab === 1 && (
          <BoxWrapper>
            <div className=" px-4 mx-auto">
              <SectionTitle>Informations sur le véhicule</SectionTitle>
              <VehicleInfoForm
                driverProfile={data?.driverProfile}
                driverId={data?.id}
                invalidateQuery={invalidateDriverDataQuery}
              />

              <SectionTitle>Documents de véhicule</SectionTitle>
              <div className="row">
                {vehicleDocs?.length > 0 ? (
                  vehicleDocs.map((doc, index) => (
                    <div className="col-xxl-3 col-md-6 mb-4" key={index}>
                      <DriverDocument
                        doc={{ ...doc, status: doc.docStatus, image: doc.src }}
                        onView={() => {
                          setOpenDocViewer(true);
                          setDocToView([doc]);
                          setDocIndex(index);
                        }}
                        onAccept={() => {
                          setDocIndex(index);
                          openAction("approveVehicleDoc");
                        }}
                        onRefuse={() => {
                          setDocIndex(index);
                          openAction("declineVehicleDoc");
                        }}
                        loading={handleDocAction.isLoading}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-center py-2">Aucun document</p>
                )}
              </div>
            </div>
          </BoxWrapper>
        )}

        {activeTab === 2 && (
          <BoxWrapper>
            <div className=" px-4 mx-auto">
              <SectionTitle>Documents de chauffeur</SectionTitle>
              <div className="row">
                {driverDocs?.length > 0 ? (
                  driverDocs.map((doc, index) => (
                    <div
                      className="col-lg-4 col-xxl-3 col-md-6 mb-4"
                      key={index}
                    >
                      <DriverDocument
                        doc={{ ...doc, status: doc.docStatus, image: doc.src }}
                        onView={() => {
                          setOpenDocViewer(true);
                          setDocToView([doc]);
                          setDocIndex(index);
                        }}
                        onAccept={() => {
                          setDocIndex(index);
                          openAction("approveDoc");
                        }}
                        onRefuse={() => {
                          setDocIndex(index);
                          openAction("declineDoc");
                        }}
                        loading={handleDocAction.isLoading}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-center py-2">Aucun document</p>
                )}
              </div>
            </div>
          </BoxWrapper>
        )}

        {activeTab === 3 && (
          <BoxWrapper>
            <div className=" px-4 mx-auto ">
              <div className="pt-4 mb-3">
                <h5 className="fw-bold text-start mb-1">Solde</h5>
                <h5 className="fw-bold text-start text-danger">
                  {data?.balance?.toFixed(2)} DH
                </h5>
                <hr className="mt-1" />
              </div>
              <Balance data={data} />
            </div>
          </BoxWrapper>
        )}

        {activeTab === 4 && <AppConfigurationTab data={data} />}

        {activeTab === 5 && (
          <BoxWrapper>
            <DriverRides driver={data} />
          </BoxWrapper>
        )}

        {/* Shared document viewer (used by vehicle + driver documents tabs) */}
        <DocViewer
          open={openDocViewer}
          close={() => {
            setOpenDocViewer(false);
            setDocToView([]);
            setDocIndex(-1);
          }}
          index={0}
          docs={docToView}
        />

        <PromptModal
          open={showModal}
          onAccept={() => handleAccpetRefuse()}
          onClose={() => setShowModal(false)}
          message={`Voulez-vous vraiment ${
            action.includes("approve")
              ? "accepter"
              : action.includes("pending")
                ? "mettre en attente"
                : "refuser"
          } ce ${action.includes("Doc") ? "document" : "profile"}?`}
        />
      </section>
    </AdminRoute>
  );
}
